import React, {FC, useCallback, useEffect, useMemo, useState} from "react";
import {useAxios} from "../../utils/hooks";
import useSafeState from "../../utils/safe-state";
import {Trans, t} from "@lingui/macro";
import {useLingui} from "@lingui/react";
import {
    Badge,
    Breadcrumb,
    Button,
    Card, Checkbox, DatePicker, Descriptions,
    Divider,
    Form,
    Input,
    List,
    message,
    Popconfirm,
    Radio,
    Select,
    Space, Spin, Tag, Tooltip,
    Upload
} from "antd";
import {
    DeleteTwoTone,
    PlusOutlined,
    UploadOutlined
} from "@ant-design/icons/lib";
import DtoTable from "../../components/dto-table/dto-table";
import ButtonWithIcon from "../../components/buttons/button-with-icon";
import MscStockModel from "../../models/mscstock/msc-stock-model";
import {openNotification} from "../../utils/openNotification";
import { useHistory } from "react-router-dom";
import {
    CodeSandboxOutlined,
    DeleteOutlined, DownloadOutlined,
    ExclamationCircleOutlined,
    FileSyncOutlined,
    FolderOpenOutlined, InfoCircleTwoTone, SketchOutlined, TeamOutlined
} from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import {AxiosResponse} from "axios";
import FormItem from "antd/es/form/FormItem";
import {useForm} from "antd/es/form/Form";
import moment from "moment/moment";

type SupplierOption = {
    code: string;
    name?: string;
    isEu?: boolean | null;
};


const MyBusinessProfileScreen: FC = () => {
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const {i18n} = useLingui();
    const [loading, setLoading] = useSafeState(false);
    const history = useHistory();
    const [form] = Form.useForm();
    const [dataLoading, setDataLoading] = useSafeState(false);
    const [supplierOptions, setSupplierOptions] = useSafeState<SupplierOption[]>([]);
    const [supplierLoading, setSupplierLoading] = useSafeState(false);
    const [collectInfoValue, setCollectInfoValue] = useState<number | undefined>(undefined);
    const [riskAssessmentValue, setRiskAssessmentValue] = useState<number | undefined>(undefined);


    const initialSupplierCode = useMemo(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }
        const stored = localStorage.getItem('supplierCode');
        if (!stored || stored === 'undefined' || stored === 'null' || stored.trim() === '') {
            return undefined;
        }
        return stored;
    }, []);

    const requiresSupplierSelection = !initialSupplierCode;
    const [selectedSupplier, setSelectedSupplier] = useSafeState<string | undefined>(initialSupplierCode);

    const getProfileData = (supplierCode: any) => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/onboarding-data/get-first-step-data?supplierCode=' + supplierCode, {})
                .then(function (response : AxiosResponse<any>) {

                    const data = response.data;
                    console.log(data);

                    if (data.sme !== undefined && data.sme !== null) {
                        data.sme = Number(data.sme);
                    }

                    if (data.collectInfo !== undefined && data.collectInfo !== null) {
                        data.collectInfo = Number(data.collectInfo);
                        setCollectInfoValue(data.collectInfo);
                    }

                    if (data.riskAssessment !== undefined && data.riskAssessment !== null) {
                        data.riskAssessment = Number(data.riskAssessment);
                        setRiskAssessmentValue(data.riskAssessment);
                    }


                    const profileValues = {
                        sme: data.sme,
                        collectInfo: data.collectInfo,
                        collectInfoDeadline: data.collectInfoDeadline ? moment(data.collectInfoDeadline) : null,
                        riskAssessment: data.riskAssessment,
                        riskAssessmentDescription: data.riskAssessmentDescription,
                        riskAssessmentDeadline: data.riskAssessmentDeadline ? moment(data.riskAssessmentDeadline) : null,
                    };

                    form.setFieldsValue({
                        onboarding: {
                            profile: profileValues
                        }
                    });
                })
                .catch(function (error) {
                    openNotification('error', error);
                })
                .finally(() => {
                });
        }
    };

    const fetchSuppliers = useCallback(() => {
        if (axiosInstance.current == null) {
            return;
        }

        setSupplierLoading(true);
        axiosInstance.current
            .get('/resource/suppliers')
            .then(response => {
                const suppliers = Array.isArray(response.data) ? response.data : [];
                setSupplierOptions(suppliers);
            })
            .catch(error => {
                openNotification('error', error);
            })
            .finally(() => {
                setSupplierLoading(false);
            });
    }, [axiosInstance, setSupplierLoading, setSupplierOptions]);

    useEffect(() => {
        if (!requiresSupplierSelection) {
            return;
        }

        if (supplierOptions.length > 0) {
            return;
        }

        fetchSuppliers();
    }, [requiresSupplierSelection, fetchSuppliers, supplierOptions.length]);

    useEffect(() => {
        if (requiresSupplierSelection && !selectedSupplier) {
            form.resetFields();
            return;
        }

        if (requiresSupplierSelection) {
            getProfileData(selectedSupplier);
        } else {
            getProfileData(initialSupplierCode);
        }
    }, [requiresSupplierSelection, selectedSupplier, initialSupplierCode]);

    const saveProfileData = () => {
        const collectInfo = form.getFieldValue(["onboarding", "profile", "collectInfo"]);
        const riskAssessment = form.getFieldValue(["onboarding", "profile", "riskAssessment"]);

        const fieldsToValidate: any[] = [
            ["onboarding", "profile", "sme"],
            ["onboarding", "profile", "collectInfo"],
            ["onboarding", "profile", "riskAssessment"]
        ];

        if (collectInfo === 2) {
            fieldsToValidate.push(["onboarding", "profile", "collectInfoDeadline"]);
        }

        if (riskAssessment === 3) {
            fieldsToValidate.push(["onboarding", "profile", "riskAssessmentDescription"]);
            fieldsToValidate.push(["onboarding", "profile", "riskAssessmentDeadline"]);
        }

        form.validateFields(fieldsToValidate).then(() => {
            if (axiosInstance.current != null) {
                axiosInstance.current
                    .post('/resource/onboarding-data/save-first-step', {
                        sme: form.getFieldValue(["onboarding", "profile", "sme"]),
                        collectInfo: form.getFieldValue(["onboarding", "profile", "collectInfo"]),
                        collectInfoDeadline: form.getFieldValue(["onboarding", "profile", "collectInfoDeadline"]) != null
                            ? moment(form.getFieldValue(["onboarding", "profile", "collectInfoDeadline"])).format('YYYY-MM-DD') : null,
                        riskAssessment: form.getFieldValue(["onboarding", "profile", "riskAssessment"]),
                        riskAssessmentDescription: form.getFieldValue(["onboarding", "profile", "riskAssessmentDescription"]),
                        riskAssessmentDeadline: form.getFieldValue(["onboarding", "profile", "riskAssessmentDeadline"]) != null
                            ? moment(form.getFieldValue(["onboarding", "profile", "riskAssessmentDeadline"])).format('YYYY-MM-DD') : null
                    }, {})
                    .then(() => {
                        openNotification('success', '');
                    })
                    .catch(function (error: any) {
                        openNotification('error', error);
                    });
            }
        }).catch((errorInfo: any) => {
            console.log('Validation failed:', errorInfo);
        });
    }

    return (<>
            <div id="alma" style={{ overflowY: "auto"}}>
                <Breadcrumb style={{marginBottom: 10}}>
                    <Breadcrumb.Item><Trans id={'Home'}/></Breadcrumb.Item>
                    <Breadcrumb.Item><Trans id={'My Business Profile'}></Trans></Breadcrumb.Item>
                </Breadcrumb>
                <Spin spinning={dataLoading}>
                    <Form form={form}>
                        {requiresSupplierSelection && (
                            <Form.Item
                                label={<Trans>Select supplier:</Trans>}
                                colon={false}
                                style={{maxWidth: 360}}
                            >
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder={i18n._(t`Select supplier to view data`)}
                                    optionFilterProp="children"
                                    value={selectedSupplier}
                                    onChange={(value) => setSelectedSupplier(typeof value === 'string' ? value : undefined)}
                                    loading={supplierLoading}
                                >
                                    {supplierOptions.map(supplier => (
                                        <Select.Option key={supplier.code} value={supplier.code}>
                                            {supplier.code}{supplier.name ? ` - ${supplier.name}` : ''}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                        <b>1. Is your organisation qualified as an SME?</b>
                        <Tooltip
                            placement="top"
                            title={
                                <>
                                    <div>
                                        As per the EUDR regulation: 'SME' means micro, small and medium-sized
                                        undertakings
                                        as defined in Article 3 of Directive 2013/34/EU of the European Parliament and
                                        of
                                        the Council
                                    </div>
                                </>
                            }
                            children={
                                <InfoCircleTwoTone
                                    twoToneColor="#1890ff"
                                    style={{marginLeft: 5}}
                                />
                            }
                        />
                        <a href="https://eur-lex.europa.eu/legal-content/EN/TXT/HTML/?uri=CELEX:02013L0034-20240528#art_3"
                           target="_blank"> About SME</a>
                        <Form.Item
                            style={{marginTop: 10}}
                            name={["onboarding", "profile", "sme"]}
                            rules={[{required: true, message: 'Please select an option'}]}>

                            <Radio.Group>
                                <Space direction="horizontal">
                                    <Radio value={1}>Yes</Radio>
                                    <Radio value={2}>No</Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>

                        <Divider></Divider>

                        <b>2. Have you collected necessary information from your suppliers in line with EUDR (EU)
                            2023/1115?</b>
                        <Form.Item
                            style={{marginTop: 10}}
                            name={["onboarding", "profile", "collectInfo"]}
                            rules={[{required: true, message: 'Please select an option'}]}>

                            <Radio.Group onChange={(e) => {
                                setCollectInfoValue(e.target.value);
                                if (e.target.value !== 2) {
                                    form.setFieldsValue({
                                        onboarding: {
                                            profile: {
                                                collectInfoDeadline: null
                                            }
                                        }
                                    });
                                }
                                form.validateFields([
                                    ["onboarding", "profile", "collectInfoDeadline"]
                                ]);
                            }}>
                                <Space direction="horizontal">
                                    <Radio value={1}>Yes</Radio>
                                    <Radio value={2}>No</Radio>
                                    <Radio value={3}>No Need</Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <span>If NO, please indicate target date to collect necessary information.</span>
                        <Form.Item
                            name={["onboarding", "profile", "collectInfoDeadline"]}
                            rules={[{required: collectInfoValue === 2, message: 'Please select a target date'}]}
                        >
                            <DatePicker style={{marginTop: 10}} disabled={collectInfoValue !== 2}/>
                        </Form.Item>
                        <Divider></Divider>

                        <b>3. Have you performed risk assessment in line with EUDR (EU) 2023/1115?</b>
                        <Form.Item
                            style={{marginTop: 10}}
                            name={["onboarding", "profile", "riskAssessment"]}
                            rules={[{required: true, message: 'Please select an option'}]}>

                            <Radio.Group
                                onChange={(e) => {
                                    setRiskAssessmentValue(e.target.value);
                                    if (e.target.value !== 3) {
                                        form.setFieldsValue({
                                            onboarding: {
                                                profile: {
                                                    riskAssessmentDescription: null,
                                                    riskAssessmentDeadline: null
                                                }
                                            }
                                        });
                                    }
                                    form.validateFields([
                                        ["onboarding", "profile", "riskAssessmentDescription"],
                                        ["onboarding", "profile", "riskAssessmentDeadline"]
                                    ]);
                                }}>
                                <Space direction="horizontal">
                                    <Radio value={1}>No, or negligible risk identified</Radio>
                                    <Radio value={2}>Identified risk is higher than negligible</Radio>
                                    <Radio value={3}>Did not perform risk assessment yet</Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <span>Describe the identified risk, if the risk is negligible or higher than negligible</span>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={["onboarding", "profile", "riskAssessmentDescription"]}
                            rules={[{
                                required: riskAssessmentValue === 2,
                                message: 'Please provide risk assessment result'
                            }]}
                        >
                            <TextArea rows={4} placeholder="Please write the risk assesment here..."
                                      disabled={riskAssessmentValue === 3}/>
                        </Form.Item>
                        <span>If you did not, please, indicate the target date for completing your risk assessment.</span>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={["onboarding", "profile", "riskAssessmentDeadline"]}
                            rules={[{
                                required: riskAssessmentValue === 3,
                                message: 'Please select a target date'
                            }]}
                        >
                            <DatePicker style={{marginTop: 10}} disabled={riskAssessmentValue !== 3}/>
                        </Form.Item>

                        {!requiresSupplierSelection && (
                            <div style={{marginTop: 10}} className="center-card">
                                <Button
                                    loading={loading}
                                    htmlType="button"
                                    type="primary"
                                    onClick={() => saveProfileData()}
                                >Save
                                </Button>
                            </div>
                        )}
                    </Form>
                </Spin>
            </div>
        </>
    )
}
export default MyBusinessProfileScreen;
